import React from "react";
import TempleCardHeader, { CardQr } from "./TempleCardHeader";

const Field = ({ label, children, testid, className = "" }) => (
  <div>
    <p className="text-gold-700 font-bold uppercase text-[7.5px] tracking-wider">{label}</p>
    <p className={`text-[10px] leading-tight ${className}`} data-testid={testid}>{children}</p>
  </div>
);

export default function TicketCard({ txn, temple, templeName, portrait = false }) {
  const t = txn.ticket || {};
  if (portrait) {
    // 4 in wide x 6 in tall card (portrait card stock): header, photo + QR side by side, details below.
    return (
      <div className="print-area bg-white border-2 border-maroon-800 rounded-md shadow-md overflow-hidden flex flex-col"
        style={{ width: "4in", height: "6in" }} data-testid="ticket-card">
        <TempleCardHeader temple={temple} templeName={templeName || txn.temple_name} title="Protocol Darshan Access Card" />
        <div className="px-3 pt-2 text-center">
          <p className="text-gold-700 font-bold uppercase text-[7.5px] tracking-wider">Ticket No</p>
          <p className="font-mono font-bold text-maroon-800 text-[15px] leading-tight" data-testid="ticket-no">{t.ticket_no}</p>
        </div>
        <div className="flex items-center justify-center gap-4 px-3 pt-2">
          {txn.photo ? (
            <img src={txn.photo} alt="devotee" className="w-[1.15in] h-[1.3in] object-cover rounded border-2 border-gold-500" data-testid="ticket-photo" />
          ) : (
            <div className="w-[1.15in] h-[1.3in] rounded border border-dashed border-gold-500 flex items-center justify-center text-[8px] text-slate-400">PHOTO</div>
          )}
          <CardQr src={t.qr_data_url} testid="ticket-qr" />
        </div>
        <div className="flex-1 px-3 pt-2 min-h-0">
          <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">
            <div className="col-span-2"><Field label="Transaction ID" testid="ticket-txn-id" className="font-mono font-bold">{txn.transaction_id}</Field></div>
            <Field label="Devotee" testid="ticket-devotee" className="font-semibold">{txn.devotee_name}</Field>
            <Field label="Reference">{txn.reference_name || "-"}</Field>
            <Field label="Darshan" testid="ticket-darshan" className="font-semibold text-maroon-800">{txn.darshan_type_name}</Field>
            <Field label="Devotees" testid="ticket-devotees" className="font-semibold">{txn.num_devotees}</Field>
            <Field label="Paid / Free" testid="ticket-paid-free">{txn.paid_free}{txn.paid_free !== "FREE" ? ` · ₹${txn.amount}` : ""}</Field>
            <Field label="Booking">{txn.booking_type === "ADVANCED" ? "Advanced" : "Current"}</Field>
            <Field label="Issued">{t.issued_at ? new Date(t.issued_at).toLocaleString("en-IN") : "-"}</Field>
            <Field label={txn.darshan_date ? "Darshan Date" : "Valid Date"} className="font-semibold text-maroon-800">
              {txn.darshan_date ? txn.darshan_date.split("-").reverse().join("/") : t.issued_at ? new Date(t.issued_at).toLocaleDateString("en-IN") : "-"}</Field>
          </div>
          <p className="mt-2 text-[7.5px] text-slate-500 leading-snug border-t border-dashed border-gold-400 pt-1">
            Carry a valid photo ID. This card is non-transferable. Entry subject to verification at the temple entrance. Follow the temple dress code.
          </p>
        </div>
        <div className="bg-maroon-800 text-gold-100 px-3 py-1 flex items-center justify-between text-[8px]">
          <span className="font-semibold text-gold-300">|| Jai Sri Ram ||</span>
          <span data-testid="ticket-footer-brand">Aatreya Infotech Systems</span>
        </div>
      </div>
    );
  }
  return (
    <div className="print-area bg-white border-2 border-maroon-800 rounded-md shadow-md overflow-hidden flex flex-col"
      style={{ width: "6in", height: "4in" }} data-testid="ticket-card">
      <TempleCardHeader temple={temple} templeName={templeName || txn.temple_name} title="Protocol Darshan Access Card" />
      <div className="flex-1 flex p-2.5 gap-3 min-h-0">
        <div className="flex-1 min-w-0 flex flex-col">
          <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">
            <Field label="Transaction ID" testid="ticket-txn-id" className="font-mono font-bold">{txn.transaction_id}</Field>
            <Field label="Ticket No" testid="ticket-no" className="font-mono font-bold text-maroon-800 text-[12px]">{t.ticket_no}</Field>
            <Field label="Devotee" testid="ticket-devotee" className="font-semibold">{txn.devotee_name}</Field>
            <Field label="Reference">{txn.reference_name || "-"}</Field>
            <Field label="Darshan" testid="ticket-darshan" className="font-semibold text-maroon-800">{txn.darshan_type_name}</Field>
            <Field label="Devotees" testid="ticket-devotees" className="font-semibold">{txn.num_devotees}</Field>
            <Field label="Paid / Free" testid="ticket-paid-free">{txn.paid_free}{txn.paid_free !== "FREE" ? ` · ₹${txn.amount}` : ""}</Field>
            <Field label="Booking">{txn.booking_type === "ADVANCED" ? "Advanced" : "Current"}</Field>
            <Field label="Issued">{t.issued_at ? new Date(t.issued_at).toLocaleString("en-IN") : "-"}</Field>
            <Field label={txn.darshan_date ? "Darshan Date" : "Valid Date"} className="font-semibold text-maroon-800">
              {txn.darshan_date ? txn.darshan_date.split("-").reverse().join("/") : t.issued_at ? new Date(t.issued_at).toLocaleDateString("en-IN") : "-"}</Field>
          </div>
          <p className="mt-auto text-[7.5px] text-slate-500 leading-snug border-t border-dashed border-gold-400 pt-1">
            Carry a valid photo ID. This card is non-transferable. Entry subject to verification at the temple entrance. Follow the temple dress code.
          </p>
        </div>
        <div className="w-[1.3in] shrink-0 flex flex-col items-center justify-between">
          {txn.photo ? (
            <img src={txn.photo} alt="devotee" className="w-[0.95in] h-[1.05in] object-cover rounded border-2 border-gold-500" data-testid="ticket-photo" />
          ) : (
            <div className="w-[0.95in] h-[1.05in] rounded border border-dashed border-gold-500 flex items-center justify-center text-[8px] text-slate-400">PHOTO</div>
          )}
          <CardQr src={t.qr_data_url} testid="ticket-qr" />
        </div>
      </div>
      <div className="bg-maroon-800 text-gold-100 px-3 py-1 flex items-center justify-between text-[8px]">
        <span className="font-semibold text-gold-300">|| Jai Sri Ram ||</span>
        <span data-testid="ticket-footer-brand">Aatreya Infotech Systems</span>
      </div>
    </div>
  );
}
